import React, { useState, useEffect } from 'react';
import { contactService } from '../../services/api';
import { FaTrash, FaSearch, FaEye, FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import Loading from '../../components/Common/Loading';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await contactService.getAll();
      
      console.log('Messages API Response:', response);
      
      let messagesData = [];
      
      if (response && response.data) {
        if (response.data.success && Array.isArray(response.data.data)) {
          messagesData = response.data.data;
        }
        else if (response.data.data && Array.isArray(response.data.data)) {
          messagesData = response.data.data;
        }
        else if (Array.isArray(response.data)) {
          messagesData = response.data;
        }
        else if (typeof response.data === 'object') {
          for (const key in response.data) {
            if (Array.isArray(response.data[key])) {
              messagesData = response.data[key];
              break;
            }
          }
        }
      }
      
      console.log('Extracted messages data:', messagesData);
      setMessages(messagesData);
      
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await contactService.markAsRead(id);
      const updatedMessages = messages.map(m => 
        m.id === id ? {...m, is_read: 1} : m
      );
      setMessages(updatedMessages);
      if (selectedMessage?.id === id) {
        setSelectedMessage({...selectedMessage, is_read: 1});
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this message?')) {
      try {
        await contactService.delete(id);
        const filteredMessages = messages.filter(m => m.id !== id);
        setMessages(filteredMessages);
        if (selectedMessage?.id === id) {
          setSelectedMessage(null);
        }
      } catch (error) {
        console.error('Error deleting message:', error);
      }
    }
  };

  const getFilteredMessages = () => {
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      return [];
    }
    
    return messages.filter(m => {
      if (!m) return false;
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = 
        (m.name?.toLowerCase().includes(searchLower) || false) ||
        (m.email?.toLowerCase().includes(searchLower) || false) ||
        (m.message?.toLowerCase().includes(searchLower) || false);
      
      let matchesFilter = true;
      if (filter === 'read') {
        matchesFilter = m.is_read === 1 || m.is_read === true;
      } else if (filter === 'unread') {
        matchesFilter = m.is_read !== 1 && m.is_read !== true;
      }
      
      return matchesSearch && matchesFilter;
    });
  };

  const filteredMessages = getFilteredMessages();
  const unreadCount = messages && Array.isArray(messages) 
    ? messages.filter(m => m && m.is_read !== 1 && m.is_read !== true).length 
    : 0;

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Contact Messages</h1>
        <p className="mt-1 text-gray-600">
          {filteredMessages.length} message(s) • {unreadCount} unread
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-3xl font-bold">{messages && Array.isArray(messages) ? messages.length : 0}</p>
              <p className="text-gray-600">Total Messages</p>
            </div>
            <FaEnvelope className="text-purple-600" size={32} />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-3xl font-bold">{unreadCount}</p>
              <p className="text-gray-600">Unread</p>
            </div>
            <FaEnvelope className="text-red-600" size={32} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:border-purple-500"
          >
            <option value="all">All Messages</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>
      </div>

      {/* Messages Table */}
      {filteredMessages.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <FaEnvelope className="mx-auto text-gray-300 mb-4" size={48} />
          <p>No messages found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sender</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredMessages.map((message) => {
                  const isRead = message.is_read === 1 || message.is_read === true;
                  return (
                    <tr key={message.id} className={`hover:bg-gray-50 ${!isRead ? 'bg-blue-50' : ''}`}>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium">{message.name || 'Anonymous'}</div>
                          <div className="text-sm text-gray-500">{message.email || '-'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-md truncate">{message.message?.substring(0, 80) || '...'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {message.created_at ? new Date(message.created_at).toLocaleString('en-US') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        {isRead ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Read</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Unread</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-3">
                          <button onClick={() => setSelectedMessage(message)} className="text-blue-600 hover:text-blue-800" title="View">
                            <FaEye />
                          </button>
                          {!isRead && (
                            <button onClick={() => markAsRead(message.id)} className="text-green-600 hover:text-green-800" title="Mark as read">
                              <FaCheckCircle />
                            </button>
                          )}
                          <button onClick={() => handleDelete(message.id)} className="text-red-600 hover:text-red-800" title="Delete">
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Message Details</h2>
              <button onClick={() => setSelectedMessage(null)} className="text-gray-500">✕</button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4 pb-4 border-b">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <FaEnvelope className="text-purple-600" />
                </div>
                <div>
                  <div className="font-semibold text-lg">{selectedMessage.name || 'Anonymous'}</div>
                  <div className="text-gray-600">{selectedMessage.email || '-'}</div>
                  <div className="text-sm text-gray-400">
                    Sent on {selectedMessage.created_at ? new Date(selectedMessage.created_at).toLocaleString('en-US') : '-'}
                  </div>
                </div>
              </div>
              
              {selectedMessage.subject && (
                <div>
                  <label className="text-gray-500 text-sm">Subject</label>
                  <p className="mt-1 font-medium">{selectedMessage.subject}</p>
                </div>
              )}
              
              <div>
                <label className="text-gray-500 text-sm">Message</label>
                <div className="mt-2 bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">
                  {selectedMessage.message || 'Content not available'}
                </div>
              </div>
              
              <div className="flex justify-end gap-4 pt-4 border-t">
                <a 
                  href={`mailto:${selectedMessage.email}`} 
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <FaEnvelope />
                  Reply
                </a>
                <button 
                  onClick={() => handleDelete(selectedMessage.id)} 
                  className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;